import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { BsFillPauseCircleFill, BsPlayCircleFill } from 'react-icons/bs';

import { LoadingOutlined } from '@ant-design/icons';
import BookImage from '@assets/images/ai4.png';
import audioPath from '@assets/sounds/flipcard.mp3';
import type { IGetStoryVoice, IStoryList } from '@coreStory/type';
import {
  useGetStoryVoice,
  useSaveStory,
  useUnSaveStory,
} from '@coreStory/useCase';
import { Spin, Dropdown, Menu } from 'antd';
import cs from 'classnames';
import { BiSkipNext, BiSkipPrevious } from 'react-icons/bi';
import { FiMoreVertical } from 'react-icons/fi';
import HTMLFlipBook from 'react-pageflip';
import styles from './index.module.scss';
import { useParams } from 'react-router-dom';
import { FaBookmark, FaRegBookmark } from 'react-icons/fa6';
import { EditStoryModal, EditTitleModal } from '../components';

interface IWebViewProps {
  storyData: IStoryList[];
  onStoryUpdated?: () => void;
  onSetVisibility?: () => void;
}

const MobileView: FC<IWebViewProps> = ({
  storyData,
  onStoryUpdated,
  onSetVisibility,
}) => {
  const { id } = useParams();
  const audioRef = useRef<HTMLAudioElement>(null);
  const voiceRef = useRef<HTMLAudioElement>(null);
  const book = useRef<any>(null);
  const [data, setData] = useState<IStoryList[]>([]);
  const [audioStorySrc, setAudioStorySrc] = useState<string>();
  const [isVoicePlaying, setIsVoicePlaying] = useState<boolean>(false);
  const [isVoiceCalled, setIsVoiceCalled] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isStorySaved, setIsStorySaved] = useState<boolean>();
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isEditTitleModalVisible, setIsEditTitleModalVisible] = useState(false);
  const [currentEditStory, setCurrentEditStory] = useState<IStoryList | null>(
    null,
  );
  const [currentEditTitleStory, setCurrentEditTitleStory] =
    useState<IStoryList | null>(null);
  const { mutate: saveStory } = useSaveStory();
  const { mutate: unSaveStory } = useUnSaveStory();

  const {
    mutate,
    data: voiceData,
    isSuccess: voiceSuccess,
    isLoading: voiceLoading,
  } = useGetStoryVoice();

  const handleSaveStory = () => {
    id &&
      saveStory(id, {
        onSuccess() {
          onStoryUpdated?.();
        },
      });
    setIsStorySaved(true);
  };

  const handleUnSaveStory = () => {
    id && unSaveStory(id);
    setIsStorySaved(false);
  };

  const handlePlay = useCallback(() => {
    audioRef?.current && audioRef.current?.play();
    voiceRef?.current && voiceRef.current.pause();
    setIsPlaying(false);
    setAudioStorySrc('');
    setIsVoiceCalled(false);
    setIsVoicePlaying(false);
    scroll(0, 300);
  }, []);

  const handleGetAudio = ({
    story_id,
    story_content_id,
    story_vorder,
    voice_speaker,
    voice_vibe,
  }: IGetStoryVoice) => {
    setIsVoiceCalled(true);
    const data = {
      story_content_id: story_id,
      story_id: story_content_id,
      story_vorder: story_vorder,
      voice_speaker: voice_speaker,
      voice_vibe: voice_vibe,
    };
    data && mutate(data);
  };

  const handleEditStory = (story: IStoryList) => {
    setCurrentEditStory(story);
    setIsEditModalVisible(true);
  };

  const handleEditStoryTitle = (story: IStoryList) => {
    setCurrentEditTitleStory(story);
    setIsEditTitleModalVisible(true);
  };

  const handleEditSuccess = () => {
    // Refresh the story data after successful edit
    // This will trigger a re-render with updated content
    setData([...data]);
    onStoryUpdated?.();
  };

  const handleEditTitleSuccess = () => {
    // Refresh the story data after successful title edit
    setData([...data]);
    onStoryUpdated?.();
  };

  const handleCancelEdit = () => {
    setIsEditModalVisible(false);
    setCurrentEditStory(null);
  };

  const handleCancelEditTitle = () => {
    setIsEditTitleModalVisible(false);
    setCurrentEditTitleStory(null);
  };

  useEffect(() => {
    if (storyData.length) {
      setData(storyData);
    }
  }, [storyData]);

  const playingButton = () => {
    if (voiceRef.current)
      if (isPlaying) {
        voiceRef.current.pause();
        setIsPlaying(false);
      } else {
        voiceRef.current.play();
        setIsPlaying(true);
      }
  };

  useEffect(() => {
    if (voiceData && voiceSuccess) {
      if (voiceRef.current) voiceRef.current.play();
      setAudioStorySrc(voiceData);
    }
  }, [voiceSuccess, voiceData]);

  return (
    <div className={styles['web_book']}>
      <audio
        style={{ display: 'none' }}
        ref={audioRef}
        src={audioPath}
        controls
      />
      {voiceRef && (
        <audio
          ref={voiceRef}
          src={`http://${voiceData}`}
          style={{ visibility: 'hidden', position: 'absolute' }}
          onEnded={() => setIsPlaying(false)}
          onPlaying={() => {
            setIsPlaying(true);
            setIsVoicePlaying(true);
          }}
          controls
        />
      )}
      <HTMLFlipBook
        onFlip={handlePlay}
        ref={book}
        className={'the-book'}
        style={{ width: '350px', margin: '0 auto' }}
        startPage={0}
        size={'stretch'}
        width={300}
        height={700}
        minWidth={300}
        maxWidth={300}
        minHeight={700}
        maxHeight={1000}
        drawShadow={true}
        flippingTime={500}
        usePortrait={true}
        startZIndex={20}
        autoSize={false}
        maxShadowOpacity={0.5}
        showCover={false}
        mobileScrollSupport={true}
        clickEventForward={true}
        useMouseEvents={false}
        swipeDistance={500}
        showPageCorners={false}
        disableFlipByClick={false}>
        {data.map((item, index) => {
          const menu = (
            <Menu>
              <Menu.Item
                key="editTitle"
                onClick={() => handleEditStoryTitle(item)}>
                Edit Title
              </Menu.Item>
              <Menu.Item key="edit" onClick={() => handleEditStory(item)}>
                Edit Content
              </Menu.Item>
              <Menu.Item key="visibility" onClick={onSetVisibility}>
                visibility
              </Menu.Item>
            </Menu>
          );

          return (
            <div
              key={index}
              className={styles['mobile_book']}
              style={{ display: 'flex' }}>
              <div className={styles['image_book']}>
                {item.StoryImage ? (
                  <img src={`http://${item.StoryImage}`} alt="Story Image" />
                ) : (
                  <img src={BookImage} alt="Default Image" />
                )}
              </div>

              <div className={styles['page_book']}>
                <div className={styles['text_book']}>
                  <p>{item?.StoryText}</p>
                </div>

                <div className={styles['audio_player']}>
                  <div
                    className={cs([
                      styles['audio_icon_Left'],
                      index === 0 ? styles['disabled'] : '',
                    ])}
                    onClick={() => book.current.pageFlip().flipPrev()}>
                    <BiSkipPrevious size={55} />
                  </div>
                  {!audioStorySrc && !isVoiceCalled ? (
                    <div
                      className={styles['icons']}
                      onClick={() =>
                        handleGetAudio({
                          story_id: item.StoryID,
                          story_content_id: item.StoryContentID ?? 0,
                          story_vorder: item.VOrder - 1,
                          voice_speaker: item.VoiceSpeaker,
                          voice_vibe: item.VoiceVibe ?? 'Auto',
                        })
                      }>
                      <BsPlayCircleFill size={60} />
                    </div>
                  ) : voiceLoading || !isVoicePlaying ? (
                    <Spin
                      indicator={
                        <LoadingOutlined
                          className={styles['audio_icon_play']}
                          style={{ fontSize: 42 }}
                          spin
                        />
                      }
                    />
                  ) : isPlaying ? (
                    <div className={styles['icons']} onClick={playingButton}>
                      <BsFillPauseCircleFill size={60} />
                    </div>
                  ) : (
                    <div className={styles['icons']} onClick={playingButton}>
                      <BsPlayCircleFill size={60} />
                    </div>
                  )}

                  <div
                    onClick={() => {
                      book.current.pageFlip().flipNext();
                    }}
                    className={cs([
                      styles['audio_icon_Right'],
                      index + 1 === data.length ? styles['disabled'] : '',
                    ])}>
                    <BiSkipNext size={55} />
                  </div>
                  <div className={styles['page_number']}>
                    <span>{item?.VOrder}</span>
                  </div>

                  <span className={styles['icon']}>
                    {isStorySaved ? (
                      <FaBookmark size={18} onClick={handleUnSaveStory} />
                    ) : (
                      <FaRegBookmark size={18} onClick={handleSaveStory} />
                    )}
                  </span>
                  {storyData[0].IsOwnerStory === 1 && (
                    <span className={styles['icon']}>
                      <Dropdown overlay={menu} trigger={['click']}>
                        <FiMoreVertical size={18} />
                      </Dropdown>
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </HTMLFlipBook>

      {/* Edit Story Modal */}
      {currentEditStory && (
        <EditStoryModal
          visible={isEditModalVisible}
          onCancel={handleCancelEdit}
          storyData={currentEditStory}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Edit Title Modal */}
      {currentEditTitleStory && (
        <EditTitleModal
          visible={isEditTitleModalVisible}
          onCancel={handleCancelEditTitle}
          storyData={currentEditTitleStory}
          onSuccess={handleEditTitleSuccess}
        />
      )}
    </div>
  );
};

export default MobileView;
